import {
  Box,
  Button,
  FormControl,
  Modal,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { BrickManagementStore } from "../../../../../../store/main/BrickManagementStore";
import { useEffect, useState } from "react";
import { FetchState } from "../../../../../../domain/models/fetch-state";
import { TerritoryDataStore } from "../../../../../../store/main/TerritoryDataStore";
import CreateBrickMapViewModal from "./CreateBrickMapViewModal";
import { CustomerDatabaseStore } from "../../../../../../store/main/CustomerDatabaseStore";
import { createBrickValidation } from "../../../../../../common/validations/validations";
import { locality } from "../../../../../../common/constants/constant";
import { head } from "lodash";

interface Props {
  open: boolean;
  handleClose: Function;
  successCallback: Function;
  hq_id: string;
  onUpdatePayload: (payload: Payload) => void;
}
interface Payload {
  hq_headquarter_id: string;
  no_of_work_days: number | string;
  name: string;
  expected_business: number | string;
  locality: string;
  distance_from_hq: string | number;
  station: string;
  distance_from_base_brick: number | string;
  base_brick_id?: string | null;
}

type FormFields = {
  brick: string;
  distance: string;
  station: string;
  expected: string;
  locality: string;
  headquarter: string;
  workdays: string;
  baseBrick: string;
  distanceFromBaseBrick: string;
};

interface Brick {
  id: number;
  name: string;
  brick_code: string;
  station: string;
}

const CreateNewBrickModal: React.FC<Props> = ({
  open,
  handleClose,
  successCallback,
  hq_id,
  onUpdatePayload,
}) => {
  const {
    fetchDistrictState,
    fetchCluster,
    addBrick,
    districtData,
    clusterData,
    BrickManagementData,
    fetchBrickManagementState,
    fetchBaseBrickLocation,
    FetchBaseBrickLocationState,
    FetchBaseBrickLocation,
  } = BrickManagementStore();
  const { headquarterMasterData, fetchBricksMaster, bricksMasterData } =
    TerritoryDataStore();

  const { filterStore, userDetails } = CustomerDatabaseStore();

  const hq = headquarterMasterData.find((hq) => hq_id === hq.id.toString());

  let totalWorkingDaysCheck = 0;
  if (BrickManagementData && BrickManagementData.length > 0) {
    if (
      BrickManagementData[0].regions &&
      BrickManagementData[0].regions.length > 0
    ) {
      totalWorkingDaysCheck =
        BrickManagementData[0].regions[0].areas[0].headquarters.reduce(
          (acc, value) => acc + value.total_no_of_work_days,
          0
        );
    }
  }

  const [brickData, setBrickData] = useState<Brick[]>([]);

  const fetchBaseBrickLocationData = async (station: string) => {
    const params = { station, headquarter_ids: [hq_id] };

    try {
      const response = await fetchBaseBrickLocation(params);

      if (!response || !response.bricks || !Array.isArray(response.bricks)) {
        setBrickData([]);
        return;
      }
      setBrickData(response?.bricks || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setBrickData([]);
    }
  };

  const handleUpdate = () => {
    const payload: Payload = {
      hq_headquarter_id: hq_id,
      no_of_work_days: watch("workdays"),
      name: watch("brick"),
      expected_business: watch("expected"),
      locality: watch("locality"),
      distance_from_hq: watch("distance"),
      station: watch("station"),
      distance_from_base_brick: distanceFromBaseBrick,
    };
    if (watch("baseBrick") !== "Self") {
      payload.base_brick_id = watch("baseBrick");
    }
    handleClose();
    successCallback();
    onUpdatePayload(payload);
  };

  const { handleSubmit, control, watch, setValue } = useForm<FormFields>({
    mode: "onChange",
  });

  const selectedStation = watch("station");
  const baseBrick = watch("baseBrick");
  const distanceFromBaseBrick = watch("distanceFromBaseBrick");

  const hasHQBrick = brickData.some((brick) => brick.station === "HQ");

  useEffect(() => {
    if (selectedStation === "HQ") {
      setValue("baseBrick", "Self");
      setValue("distanceFromBaseBrick", "0");
      setValue("distance", "0");
    } else if (selectedStation === "EX" && baseBrick) {
      const selectedBrick = brickData.find(
        (brick) => brick.id === Number(baseBrick)
      );
      if (selectedBrick?.station === "HQ") {
        setValue("distance", distanceFromBaseBrick);
      }
    }
  }, [selectedStation, baseBrick, distanceFromBaseBrick]);

  useEffect(() => {
    if (selectedStation) {
      fetchBaseBrickLocationData(selectedStation);
    }
  }, [selectedStation]);

  return (
    <Modal open={open} onClose={() => handleClose()}>
      <Box
        position={"absolute" as "absolute"}
        top={"50%"}
        left={"50%"}
        bgcolor={"background.paper"}
        borderRadius={"22px"}
        sx={{ transform: "translate(-50%, -50%)" }}
        width={"25%"}
        p={3}
      >
        <Typography
          fontWeight={500}
          variant="h6"
          textAlign="center"
          marginBottom={2}
        >
          Create New Brick
        </Typography>
        <Stack spacing={2}>
          <Controller
            name="brick"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl size="small" fullWidth>
                <TextField
                  fullWidth
                  id="outlined-basic"
                  label="Brick Name"
                  value={value}
                  size="small"
                  onChange={onChange}
                  error={!!error}
                  helperText={error?.message}
                />
              </FormControl>
            )}
            rules={createBrickValidation.brickName}
          />
          <Controller
            name="headquarter"
            control={control}
            defaultValue={hq?.name}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl size="small" fullWidth>
                <TextField
                  fullWidth
                  id="outlined-disabled"
                  label="Headquarter Name"
                  value={value}
                  size="small"
                  disabled
                />
              </FormControl>
            )}
          />

          {/* <Controller
            name="workdays"
            control={control}
            rules={{
              ...createBrickValidation.workDays,
              validate: (newWorkDays) => {
                const numWorkDays = Number(newWorkDays);

                if (!numWorkDays) return "No of Work Days is required";
                if (numWorkDays === 0) return "Work Days cannot be 0";
                if (numWorkDays > 3)
                  return "Working Days should not exceed beyond 3 days";
                // if (numWorkDays > 24) {
                //   return "Your working days limit of 24 is reached";
                // }
                return true;
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const numWorkDays = Number(value);
              const totalWorkDays =
                totalWorkingDaysCheck + (isNaN(numWorkDays) ? 0 : numWorkDays);

              return (
                <FormControl size="small" fullWidth>
                  <TextField
                    fullWidth
                    id="outlined-basic"
                    label="No of Work Days"
                    value={value}
                    size="small"
                    onChange={(event) => onChange(event.target.value)}
                    placeholder="No of Work Days"
                    error={!!error}
                    helperText={error?.message}
                  />
                  <Typography
                    variant="body2"
                    color={totalWorkDays > 24 ? "error" : "textSecondary"}
                    sx={{
                      mt: 1,
                      color: totalWorkDays > 24 ? "error.main" : "#42a5f5",
                    }}
                  >
                    Total Working Days: {totalWorkDays}
                  </Typography>
                </FormControl>
              );
            }}
          /> */}

          <Controller
            name="workdays"
            control={control}
            rules={{
              validate: (newWorkDays) => {
                const numWorkDays = Number(newWorkDays);
                const totalWorkDays =
                  totalWorkingDaysCheck +
                  (isNaN(numWorkDays) ? 0 : numWorkDays);

                if (!numWorkDays) return "No of Work Days is required";
                if (numWorkDays === 0) return "Work Days cannot be 0";

                if (totalWorkingDaysCheck > 24) {
                 
                  if (totalWorkDays > totalWorkingDaysCheck) {
                    return "You can only decrease the workdays to 24 or less";
                  }
                } else {
                 
                  if (totalWorkDays > 24) {
                    return "Your working days limit of 24 is reached";
                  }
                }

                return true;
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const numWorkDays = Number(value);
              const totalWorkDays =
                totalWorkingDaysCheck + (isNaN(numWorkDays) ? 0 : numWorkDays);

              return (
                <FormControl size="small" fullWidth>
                  <TextField
                    fullWidth
                    id="outlined-basic"
                    label="No of Work Days"
                    value={value}
                    size="small"
                    onChange={(event) => onChange(event.target.value)}
                    placeholder="No of Work Days"
                    error={!!error}
                    helperText={error?.message}
                  />
                  <Typography
                    variant="body2"
                    color={totalWorkDays > 24 ? "error" : "textSecondary"}
                    sx={{
                      mt: 1,
                      color: totalWorkDays > 24 ? "error.main" : "#42a5f5",
                    }}
                  >
                    Total Working Days: {totalWorkDays}
                  </Typography>
                </FormControl>
              );
            }}
          />

          <Stack direction={"row"} gap={2}>
            <Controller
              name="station"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="select-label">EX/OS/HQ</InputLabel>
                  <Select
                    labelId="select-label"
                    id="select"
                    value={value}
                    onChange={onChange}
                    displayEmpty
                    label="EX/OS/HQ"
                  >
                    <MenuItem value="EX">EX</MenuItem>
                    <MenuItem value="OS">OS</MenuItem>
                    <MenuItem value="HQ">HQ</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="distance"
              control={control}
              defaultValue={watch("station") === "HQ" ? "0" : ""}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl size="small" fullWidth>
                  <TextField
                    fullWidth
                    focused
                    id="outlined-required"
                    label="Distance from HQ"
                    value={
                      selectedStation === "HQ"
                        ? "0"
                        : baseBrick &&
                          brickData.find(
                            (brick) => brick.id === Number(baseBrick)
                          )?.station === "HQ"
                        ? distanceFromBaseBrick
                        : value
                    }
                    size="small"
                    disabled={
                      selectedStation === "HQ" ||
                      !!(
                        baseBrick &&
                        brickData.find(
                          (brick) => brick.id === Number(baseBrick)
                        )?.station === "HQ"
                      )
                    }
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                  />
                </FormControl>
              )}
              rules={createBrickValidation.distance}
            />
          </Stack>
          <Stack direction={"row"} gap={2}>
            {selectedStation && selectedStation === "HQ" ? (
              <TextField
                fullWidth
                label="Base Brick"
                value="Self"
                disabled
                InputProps={{ style: { height: 40 } }}
                InputLabelProps={{
                  shrink: selectedStation === "HQ" ? true : false,
                }}
              />
            ) : (
              <Controller
                name="baseBrick"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControl
                    fullWidth
                    size="small"
                    error={selectedStation === "OS" && !hasHQBrick}
                  >
                    <InputLabel>Base Brick</InputLabel>
                    <Select value={value} onChange={onChange}>
                      {brickData.map((brick) => (
                        <MenuItem key={brick.id} value={brick.id}>
                          {brick.name} -{" "}
                          <Typography
                            component="span"
                            fontWeight="bold"
                            sx={{
                              backgroundColor: "#f0f0f0",
                              px: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {brick.station}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                    {selectedStation === "OS" && !hasHQBrick && (
                      <FormHelperText>
                        Please add an HQ location first
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            )}
            <Controller
              name="distanceFromBaseBrick"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl size="small" fullWidth>
                  <TextField
                    fullWidth
                    id="outlined-required"
                    label="Distance from Base Brick"
                    value={value}
                    size="small"
                    disabled={selectedStation === "HQ"}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                </FormControl>
              )}
              rules={createBrickValidation.distanceFromBaseBrick}
            />
          </Stack>
          <Stack direction={"row"} gap={2}>
            <Controller
              name="expected"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl size="small" fullWidth>
                  <TextField
                    fullWidth
                    value={value}
                    id="outlined-basic"
                    label="Expected Business"
                    size="small"
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CurrencyRupeeIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              )}
              rules={createBrickValidation.expectedBusiness}
            />
            <Controller
              name="locality"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="select-label">Locality</InputLabel>
                  <Select
                    labelId="select-label"
                    id="select"
                    value={value}
                    onChange={onChange}
                    displayEmpty
                    label="Locality"
                  >
                    <MenuItem value="metro">{locality.METRO}</MenuItem>
                    <MenuItem value="semi_metro">
                      {locality.SEMI_METRO}
                    </MenuItem>
                    <MenuItem value="others">{locality.OTHERS}</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Stack>
        </Stack>
        <Stack
          paddingTop={3}
          direction={"row"}
          gap={3}
          justifyContent={"center"}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(handleUpdate)}
            disabled={
              !(
                watch("brick") &&
                watch("headquarter") &&
                (watch("station") === "HQ" ? true : watch("distance")) &&
                watch("station") &&
                watch("baseBrick") &&
                (watch("baseBrick") === "Self"
                  ? true
                  : watch("distanceFromBaseBrick")) &&
                watch("expected") &&
                watch("locality")
              )
            }
            sx={{
              textTransform: "unset",
              borderRadius: "30px",
              minWidth: "30%",
            }}
          >
            Next
          </Button>

          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleClose()}
            sx={{
              textTransform: "unset",
              borderRadius: "30px",
              minWidth: "30%",
            }}
          >
            Cancel
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default CreateNewBrickModal;
